/**
 * Portal registry — which role reaches which portal, and each portal's navigation.
 *
 * UI-level visibility only. Real enforcement happens in route guards
 * (RequirePortal) and, authoritatively, in PostgreSQL RLS.
 */
import type { IconKey } from '@/design-system/components/Icon';
import type { PortalMessages } from '@/i18n/portal/en';
import { hasPermission, type AccessProfile, type PermissionCode, type RoleCode } from './types';

export type PortalKey =
  | 'executive'
  | 'admin'
  | 'finance'
  | 'hr'
  | 'marketing'
  | 'workshop'
  | 'inventory'
  | 'logistics'
  | 'pos'
  | 'qa'
  | 'audit'
  | 'employee'
  | 'business'
  | 'vendor'
  | 'customer'
  | 'tracking';

export type NavLabelKey = keyof PortalMessages['portal']['nav'];
export type NavGroupKey = keyof PortalMessages['portal']['groups'];
export type IconName = IconKey;

export interface NavItem {
  /** Path segment relative to the portal base ('' = portal home). Absolute paths start with '/'. */
  path: string;
  label: NavLabelKey;
  icon: IconName;
  /** Approved module number (1–25) this item belongs to. */
  module?: number;
  /** Hidden unless the user holds this permission (defence in depth; RLS still decides). */
  permission?: PermissionCode;
  /** True when a working screen is delivered; false renders the module foundation page. */
  implemented: boolean;
  /**
   * 2 = Milestone 2 / Advanced Q27 scope, NOT authorized under the current contract
   * (CLAUDE.md §3). Such items only ever render a clearly labelled future capability.
   */
  milestone?: 2;
  /** Sidebar group (portals with many sections, e.g. the admin control center). */
  group?: NavGroupKey;
}

export interface PortalDefinition {
  key: PortalKey;
  basePath: string;
  /** Roles whose holders may enter the portal. */
  roles: readonly RoleCode[];
  /** Holders of this permission may also enter (e.g. an administrator with portal access). */
  permission?: PermissionCode;
  nav: readonly NavItem[];
}

const INTERNAL_OWNER: readonly RoleCode[] = ['owner'];

export const PORTALS: Record<PortalKey, PortalDefinition> = {
  executive: {
    key: 'executive',
    basePath: '/portal/executive',
    roles: ['owner'],
    permission: 'portal.executive',
    nav: [
      { path: '', label: 'dashboard', icon: 'grid', module: 19, implemented: true },
      { path: 'branches', label: 'branchPerformance', icon: 'mapPin', module: 19, permission: 'executive.analytics_read', implemented: false },
      { path: 'reports', label: 'reports', icon: 'chart', module: 20, permission: 'reports.read', implemented: false },
      { path: 'search', label: 'globalSearch', icon: 'search', module: 20, permission: 'reports.read', implemented: false },
    ],
  },
  admin: {
    key: 'admin',
    basePath: '/portal/admin',
    roles: ['system_admin', ...INTERNAL_OWNER],
    permission: 'portal.admin',
    nav: [
      { path: '', label: 'controlCenter', icon: 'grid', module: 24, implemented: true, group: 'start' },
      { path: 'presentation', label: 'presentation', icon: 'play', module: 24, permission: 'portal.preview', implemented: false, group: 'start' },
      { path: 'preview', label: 'portalPreview', icon: 'scan', module: 22, permission: 'portal.preview', implemented: true, group: 'start' },
      { path: 'reports', label: 'reports', icon: 'chart', module: 20, permission: 'reports.read', implemented: false, group: 'start' },
      { path: 'users', label: 'users', icon: 'users', module: 22, permission: 'users.read', implemented: true, group: 'people' },
      { path: 'roles', label: 'rolesBundles', icon: 'shield', module: 22, permission: 'users.read', implemented: true, group: 'people' },
      { path: 'access-log', label: 'accessLog', icon: 'history', module: 22, permission: 'users.read', implemented: true, group: 'people' },
      { path: 'identities', label: 'companyIdentities', icon: 'mail', module: 22, permission: 'identity.read', implemented: true, group: 'people' },
      { path: 'customers', label: 'customers', icon: 'user', module: 1, permission: 'customers.read_all', implemented: false, group: 'people' },
      { path: 'catalog', label: 'catalog', icon: 'tag', module: 3, permission: 'catalog.manage', implemented: true, group: 'catalog' },
      { path: 'services', label: 'servicesPrices', icon: 'wrench', module: 5, permission: 'services.manage', implemented: true, group: 'catalog' },
      { path: 'vehicle-assets', label: 'vehicleAssets', icon: 'car', module: 2, permission: 'vehicle_master.manage', implemented: true, group: 'catalog' },
      { path: 'media', label: 'mediaAssets', icon: 'layers', module: 21, permission: 'files.manage', implemented: false, group: 'catalog' },
      { path: 'centers', label: 'centers', icon: 'mapPin', module: 24, permission: 'branches.manage', implemented: false, group: 'network' },
      { path: 'bookings', label: 'bookings', icon: 'calendar', module: 5, permission: 'bookings.read_all', implemented: false, group: 'operations' },
      { path: '/portal/inventory', label: 'inventory', icon: 'box', module: 9, permission: 'inventory.read', implemented: true, group: 'operations' },
      { path: 'suppliers', label: 'suppliers', icon: 'handshake', module: 12, permission: 'suppliers.read', implemented: true, group: 'operations' },
      { path: 'franchises', label: 'franchises', icon: 'handshake', module: 15, permission: 'franchise.read_all', implemented: true, group: 'operations' },
      { path: 'warranty', label: 'warranties', icon: 'shield', module: 4, permission: 'warranties.read_all', implemented: false, group: 'operations' },
      { path: '/portal/marketing/campaigns', label: 'campaigns', icon: 'megaphone', module: 17, permission: 'marketing.campaigns_manage', implemented: true, group: 'engagement' },
      { path: 'notifications', label: 'notifications', icon: 'bell', module: 7, permission: 'notifications.send', implemented: true, group: 'engagement' },
      { path: 'communications', label: 'communications', icon: 'mail', module: 22, permission: 'communications.read_all', implemented: true, group: 'engagement' },
      { path: 'assistant', label: 'assistantKnowledge', icon: 'lifebuoy', module: 7, permission: 'assistant.knowledge_manage', implemented: true, group: 'engagement' },
      { path: 'support', label: 'supportTickets', icon: 'lifebuoy', module: 7, permission: 'support.read', implemented: true, group: 'engagement' },
      { path: 'integrations', label: 'integrations', icon: 'plug', module: 24, permission: 'integrations.manage', implemented: true, group: 'platform' },
      { path: 'payments', label: 'payments', icon: 'card', module: 6, permission: 'integrations.manage', implemented: true, group: 'platform' },
      { path: 'legacy', label: 'legacyData', icon: 'archive', module: 23, permission: 'odoo.read', implemented: true, group: 'platform' },
      { path: 'audit', label: 'audit', icon: 'history', module: 21, permission: 'audit.read', implemented: true, group: 'platform' },
      { path: 'settings', label: 'settings', icon: 'settings', module: 24, permission: 'settings.manage', implemented: true, group: 'platform' },
    ],
  },
  finance: {
    key: 'finance',
    basePath: '/portal/finance',
    roles: ['finance', ...INTERNAL_OWNER],
    permission: 'portal.finance',
    nav: [
      { path: '', label: 'overview', icon: 'grid', module: 8, implemented: true },
      { path: 'invoices', label: 'invoices', icon: 'file', module: 8, permission: 'collections.read_all', implemented: true },
      { path: 'receivables', label: 'receivables', icon: 'coins', module: 8, permission: 'collections.read_all', implemented: true },
      { path: 'collections', label: 'collections', icon: 'history', module: 8, permission: 'collections.manage', implemented: true },
      { path: 'payments', label: 'payments', icon: 'card', module: 6, permission: 'payments.read_all', implemented: true },
      { path: 'reconciliation', label: 'reconciliation', icon: 'check', module: 6, permission: 'payments.manage', implemented: true },
      { path: 'terms', label: 'paymentTerms', icon: 'clock', module: 8, permission: 'collections.manage', implemented: true },
      { path: 'reports', label: 'reports', icon: 'chart', module: 20, permission: 'reports.read', implemented: true },
    ],
  },
  hr: {
    key: 'hr',
    basePath: '/portal/hr',
    roles: ['hr', ...INTERNAL_OWNER],
    permission: 'portal.hr',
    nav: [
      { path: '', label: 'overview', icon: 'grid', module: 16, implemented: true },
      { path: 'employees', label: 'employees', icon: 'users', module: 16, permission: 'hr.read_all', implemented: true },
      { path: 'leave', label: 'leave', icon: 'calendar', module: 16, permission: 'hr.read_all', implemented: true },
      { path: 'balances', label: 'leaveBalances', icon: 'clock', module: 16, permission: 'hr.read_all', implemented: true },
      { path: 'entitlements', label: 'entitlements', icon: 'file', module: 16, permission: 'hr.read_all', implemented: true },
      { path: 'documents', label: 'documents', icon: 'archive', module: 16, permission: 'hr.read_all', implemented: false },
      { path: 'reports', label: 'reports', icon: 'chart', module: 20, permission: 'hr.read_all', implemented: true },
    ],
  },
  marketing: {
    key: 'marketing',
    basePath: '/portal/marketing',
    roles: ['marketing', ...INTERNAL_OWNER],
    permission: 'portal.marketing',
    nav: [
      { path: '', label: 'overview', icon: 'grid', module: 18, implemented: true },
      { path: 'attribution', label: 'attribution', icon: 'chart', module: 18, permission: 'marketing.analytics_read', implemented: true },
      { path: 'sources', label: 'trafficSources', icon: 'globe', module: 18, permission: 'marketing.analytics_read', implemented: true },
      { path: 'campaigns', label: 'campaigns', icon: 'megaphone', module: 17, permission: 'marketing.campaigns_manage', implemented: true },
      { path: 'journeys', label: 'customerJourneys', icon: 'navigation', module: 18, permission: 'marketing.analytics_read', implemented: true },
      { path: 'leads', label: 'leads', icon: 'users', module: 18, permission: 'marketing.leads_read', implemented: true },
      { path: 'connectors', label: 'adConnectors', icon: 'plug', module: 18, permission: 'marketing.analytics_read', implemented: true },
      { path: 'reports', label: 'reports', icon: 'chart', module: 20, permission: 'marketing.analytics_read', implemented: true },
    ],
  },
  workshop: {
    key: 'workshop',
    basePath: '/portal/workshop',
    roles: ['workshop', ...INTERNAL_OWNER],
    permission: 'portal.workshop',
    nav: [
      { path: '', label: 'overview', icon: 'grid', module: 5, implemented: true },
      { path: 'jobs', label: 'installationJobs', icon: 'wrench', module: 5, permission: 'workshop.read', implemented: true },
      { path: 'bookings', label: 'appointmentsWithoutJob', icon: 'calendar', module: 5, permission: 'workshop.read', implemented: true },
      { path: 'warranties', label: 'warranties', icon: 'shield', module: 4, permission: 'warranties.read_all', implemented: true },
      { path: 'inventory', label: 'inventory', icon: 'box', module: 9, permission: 'inventory.read', implemented: true },
      { path: 'work-orders', label: 'workOrders', icon: 'wrench', module: 11, permission: 'workshop.read', implemented: false, milestone: 2 },
      { path: 'conversion', label: 'conversion', icon: 'car', module: 10, permission: 'workshop.read', implemented: false, milestone: 2 },
      { path: 'q27', label: 'q27', icon: 'car', module: 11, permission: 'workshop.read', implemented: false, milestone: 2 },
      { path: 'technicians', label: 'technicians', icon: 'users', module: 11, permission: 'workshop.read', implemented: false, milestone: 2 },
      { path: 'materials', label: 'materials', icon: 'box', module: 11, permission: 'workshop.read', implemented: false, milestone: 2 },
      { path: 'qc-handover', label: 'qcHandover', icon: 'check', module: 11, permission: 'workshop.read', implemented: false, milestone: 2 },
    ],
  },
  inventory: {
    key: 'inventory',
    basePath: '/portal/inventory',
    roles: ['inventory', ...INTERNAL_OWNER],
    permission: 'portal.inventory',
    nav: [
      { path: '', label: 'overview', icon: 'grid', module: 9, implemented: true },
      { path: 'stock', label: 'stock', icon: 'box', module: 9, permission: 'inventory.read', implemented: true },
      { path: 'rolls', label: 'rollsPackages', icon: 'layers', module: 9, permission: 'inventory.read', implemented: true },
      { path: 'batches', label: 'batches', icon: 'archive', module: 9, permission: 'inventory.read', implemented: true },
      { path: 'movements', label: 'movements', icon: 'history', module: 9, permission: 'inventory.read', implemented: true },
      { path: 'receiving', label: 'receivedStock', icon: 'truck', module: 9, permission: 'inventory.read', implemented: true },
      { path: 'allocations', label: 'allocations', icon: 'cart', module: 9, permission: 'inventory.read', implemented: true },
      { path: 'traceability', label: 'traceability', icon: 'qr', module: 9, permission: 'inventory.read', implemented: true },
      { path: 'alerts', label: 'lowStock', icon: 'alert', module: 9, permission: 'inventory.read', implemented: true },
      { path: 'reports', label: 'reports', icon: 'chart', module: 20, permission: 'inventory.read', implemented: true },
    ],
  },
  pos: {
    key: 'pos',
    basePath: '/portal/pos',
    roles: ['pos', ...INTERNAL_OWNER],
    permission: 'portal.pos',
    nav: [
      { path: '', label: 'register', icon: 'cart', module: 6, permission: 'pos.sell', implemented: true },
      { path: 'sales', label: 'posSales', icon: 'history', module: 6, permission: 'pos.read', implemented: true },
    ],
  },
  logistics: {
    key: 'logistics',
    basePath: '/portal/logistics',
    roles: ['logistics', ...INTERNAL_OWNER],
    permission: 'portal.logistics',
    nav: [
      { path: '', label: 'overview', icon: 'grid', module: 13, implemented: true },
      { path: 'shipments', label: 'shipments', icon: 'truck', module: 13, permission: 'logistics.read', implemented: true },
      { path: 'tracking', label: 'trackingView', icon: 'navigation', module: 13, permission: 'logistics.read', implemented: true },
      { path: 'deliveries', label: 'deliveries', icon: 'check', module: 13, permission: 'logistics.read', implemented: true },
      { path: 'vehicle-transport', label: 'vehicleTransport', icon: 'car', module: 13, permission: 'logistics.read', implemented: false, milestone: 2 },
      { path: 'inspections', label: 'conditionInspections', icon: 'scan', module: 13, permission: 'logistics.read', implemented: false, milestone: 2 },
      { path: 'transporters', label: 'transporters', icon: 'users', module: 13, permission: 'logistics.read', implemented: false, milestone: 2 },
      { path: 'q27-logistics', label: 'q27Logistics', icon: 'truck', module: 13, permission: 'logistics.read', implemented: false, milestone: 2 },
    ],
  },
  qa: {
    key: 'qa',
    basePath: '/portal/qa',
    roles: ['qa', ...INTERNAL_OWNER],
    permission: 'portal.qa',
    nav: [
      { path: '', label: 'overview', icon: 'grid', module: 4, implemented: true },
      { path: 'checks', label: 'qualityChecks', icon: 'scan', module: 4, permission: 'qa.read', implemented: true },
      { path: 'complaints', label: 'complaints', icon: 'alert', module: 4, permission: 'qa.read', implemented: true },
      { path: 'warranty-claims', label: 'warrantyQuality', icon: 'shield', module: 4, permission: 'qa.read', implemented: true },
      { path: 'satisfaction', label: 'satisfaction', icon: 'users', module: 4, permission: 'qa.read', implemented: true },
      { path: 'defects', label: 'recurringDefects', icon: 'layers', module: 4, permission: 'qa.read', implemented: true },
      { path: 'actions', label: 'correctiveActions', icon: 'wrench', module: 4, permission: 'qa.read', implemented: true },
      { path: 'reports', label: 'reports', icon: 'chart', module: 20, permission: 'qa.read', implemented: true },
    ],
  },
  audit: {
    key: 'audit',
    basePath: '/portal/audit',
    roles: ['auditor', ...INTERNAL_OWNER],
    permission: 'portal.audit',
    nav: [
      { path: '', label: 'overview', icon: 'grid', module: 21, implemented: true },
      { path: 'engagements', label: 'auditEngagements', icon: 'briefcase', module: 21, permission: 'audit.read', implemented: true },
      { path: 'findings', label: 'findings', icon: 'alert', module: 21, permission: 'audit.read', implemented: true },
      { path: 'follow-up', label: 'followUp', icon: 'clock', module: 21, permission: 'audit.read', implemented: true },
      { path: 'evidence', label: 'evidence', icon: 'file', module: 21, permission: 'audit.read', implemented: true },
      { path: 'approvals', label: 'approvals', icon: 'check', module: 21, permission: 'audit.read', implemented: true },
      { path: 'reports', label: 'auditReports', icon: 'chart', module: 21, permission: 'audit.read', implemented: true },
      { path: 'trail', label: 'auditTrail', icon: 'history', module: 21, permission: 'audit.read', implemented: true },
      { path: 'activity', label: 'userActivity', icon: 'users', module: 21, permission: 'audit.read', implemented: true },
      { path: 'access', label: 'accessLog', icon: 'shield', module: 22, permission: 'audit.read', implemented: true },
    ],
  },
  employee: {
    key: 'employee',
    basePath: '/portal/employee',
    roles: ['employee', 'hr', ...INTERNAL_OWNER],
    permission: 'portal.employee',
    nav: [
      { path: '', label: 'overview', icon: 'grid', module: 16, implemented: true },
      { path: 'profile', label: 'myProfile', icon: 'user', module: 16, implemented: true },
      { path: 'leave', label: 'myLeave', icon: 'calendar', module: 16, implemented: true },
      { path: 'entitlements', label: 'entitlements', icon: 'file', module: 16, implemented: true },
      { path: 'audit-actions', label: 'myAuditActions', icon: 'alert', module: 21, implemented: true },
      { path: 'tasks', label: 'myTasks', icon: 'wrench', module: 5, implemented: false },
      { path: 'notifications', label: 'notifications', icon: 'bell', module: 7, implemented: true },
      { path: 'tickets', label: 'myTickets', icon: 'lifebuoy', module: 7, implemented: true },
      { path: 'mail', label: 'mail', icon: 'mail', module: 22, implemented: true },
      { path: 'meetings', label: 'meetings', icon: 'calendar', module: 22, implemented: true },
      { path: 'resources', label: 'resources', icon: 'archive', module: 16, implemented: false },
    ],
  },
  business: {
    key: 'business',
    basePath: '/portal/business',
    roles: ['franchise'],
    nav: [
      { path: '', label: 'overview', icon: 'grid', module: 15, implemented: true },
      { path: 'transactions', label: 'purchases', icon: 'coins', module: 15, implemented: true },
      { path: 'quality', label: 'quality', icon: 'file', module: 15, implemented: true },
      { path: 'complaints', label: 'complaints', icon: 'alert', module: 15, implemented: true },
      { path: 'warranties', label: 'warranties', icon: 'shield', module: 4, implemented: true },
      { path: 'performance', label: 'performance', icon: 'chart', module: 15, implemented: true },
      { path: 'orders', label: 'orders', icon: 'cart', module: 6, implemented: false },
      { path: 'products', label: 'products', icon: 'tag', module: 3, implemented: false },
      { path: 'bookings', label: 'bookings', icon: 'calendar', module: 5, implemented: false },
      { path: 'documents', label: 'documents', icon: 'archive', module: 15, implemented: false },
      { path: 'messages', label: 'messages', icon: 'lifebuoy', module: 15, implemented: false },
    ],
  },
  vendor: {
    key: 'vendor',
    basePath: '/portal/vendor',
    roles: ['vendor'],
    nav: [
      { path: '', label: 'overview', icon: 'grid', module: 12, implemented: true },
      { path: 'profile', label: 'supplierProfile', icon: 'briefcase', module: 12, implemented: true },
      { path: 'contacts', label: 'contacts', icon: 'users', module: 12, implemented: true },
      { path: 'documents', label: 'documents', icon: 'archive', module: 12, implemented: true },
      { path: 'messages', label: 'messages', icon: 'lifebuoy', module: 12, implemented: true },
      { path: 'history', label: 'relationshipHistory', icon: 'history', module: 12, implemented: true },
      { path: 'rfqs', label: 'rfqs', icon: 'file', module: 12, implemented: false, milestone: 2 },
      { path: 'quotations', label: 'quotationSubmission', icon: 'file', module: 12, implemented: false, milestone: 2 },
      { path: 'purchase-orders', label: 'purchaseOrders', icon: 'cart', module: 12, implemented: false, milestone: 2 },
      { path: 'receiving', label: 'supplierReceiving', icon: 'truck', module: 12, implemented: false, milestone: 2 },
      { path: 'claims', label: 'supplierClaims', icon: 'alert', module: 12, implemented: false, milestone: 2 },
      { path: 'supplier-warranty', label: 'supplierWarranty', icon: 'shield', module: 12, implemented: false, milestone: 2 },
    ],
  },
  customer: {
    key: 'customer',
    basePath: '/account',
    roles: ['customer'],
    nav: [
      { path: '', label: 'overview', icon: 'grid', module: 1, implemented: true },
      { path: 'vehicles', label: 'myVehicles', icon: 'car', module: 2, implemented: true },
      { path: '/book', label: 'bookService', icon: 'calendar', module: 5, implemented: true },
      { path: 'bookings', label: 'myBookings', icon: 'history', module: 5, implemented: true },
      { path: 'quotes', label: 'myQuotes', icon: 'file', module: 6, implemented: true },
      { path: 'orders', label: 'myOrders', icon: 'cart', module: 6, implemented: true },
      { path: 'payments', label: 'myPayments', icon: 'card', module: 6, implemented: true },
      { path: 'warranty', label: 'myWarranty', icon: 'shield', module: 4, implemented: true },
      { path: 'journey', label: 'myJourney', icon: 'wrench', module: 11, implemented: true },
      { path: '/verify', label: 'qrVerify', icon: 'qr', module: 4, implemented: true },
      { path: '/track', label: 'trackShipment', icon: 'truck', module: 13, implemented: true },
      { path: 'offers', label: 'offers', icon: 'tag', module: 7, implemented: false },
      { path: '/contact', label: 'support', icon: 'lifebuoy', implemented: true },
      { path: 'profile', label: 'profile', icon: 'user', module: 22, implemented: true },
    ],
  },
  tracking: {
    key: 'tracking',
    basePath: '/track',
    roles: ['shipment_customer'],
    nav: [],
  },
};

export function canAccessPortal(access: AccessProfile | null, key: PortalKey): boolean {
  if (!access || access.status !== 'active') return false;
  const portal = PORTALS[key];
  if (portal.roles.some((role) => access.roles.includes(role))) return true;
  return !!portal.permission && access.permissions.includes(portal.permission);
}

/** The portal a role lands in (its home portal); null when it has none. */
export function portalForRole(role: RoleCode): PortalDefinition | null {
  const home = (Object.values(PORTALS) as PortalDefinition[]).find((p) => p.roles[0] === role);
  return home ?? (Object.values(PORTALS) as PortalDefinition[]).find((p) => p.roles.includes(role)) ?? null;
}

/** Sidebar items split into their groups (in order); ungrouped portals return one unnamed group. */
export function groupNav(items: NavItem[]): { group: NavGroupKey | null; items: NavItem[] }[] {
  const groups: { group: NavGroupKey | null; items: NavItem[] }[] = [];
  for (const item of items) {
    const key = item.group ?? null;
    const last = groups[groups.length - 1];
    if (last && last.group === key) last.items.push(item);
    else groups.push({ group: key, items: [item] });
  }
  return groups;
}

export function accessiblePortals(access: AccessProfile | null): PortalDefinition[] {
  return (Object.keys(PORTALS) as PortalKey[])
    .filter((key) => key !== 'tracking' && canAccessPortal(access, key))
    .map((key) => PORTALS[key]);
}

/** Navigation items the user is permitted to see. */
export function visibleNav(access: AccessProfile | null, portal: PortalDefinition): NavItem[] {
  return portal.nav.filter((item) => !item.permission || hasPermission(access, item.permission));
}

/** Sidebar items: permitted items; screens not yet delivered are hidden in production. */
export function sidebarItems(access: AccessProfile | null, portal: PortalDefinition, isProduction: boolean): NavItem[] {
  return visibleNav(access, portal).filter((item) => !isProduction || item.implemented || item.path === '');
}

export function navHref(portal: PortalDefinition, item: NavItem): string {
  if (item.path.startsWith('/')) return item.path;
  return item.path ? `${portal.basePath}/${item.path}` : portal.basePath;
}

/** Only same-origin relative paths are allowed as post-login redirects (prevents open redirects). */
export function isSafeRedirect(path: string | null | undefined): path is string {
  if (!path) return false;
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return false;
  if (/^\/(login|register|auth)(\/|$|\?)/.test(path)) return false;
  return ![...path].some((ch) => ch.charCodeAt(0) < 32);
}

/** Portal that owns a given path, if any. */
export function portalForPath(path: string): PortalDefinition | null {
  const matches = (Object.values(PORTALS) as PortalDefinition[])
    .filter((p) => p.key !== 'tracking' && (path === p.basePath || path.startsWith(`${p.basePath}/`)))
    .sort((a, b) => b.basePath.length - a.basePath.length);
  return matches[0] ?? null;
}

/**
 * Where to send a user after sign-in.
 * Honors a safe `next` path when the user may access it; otherwise uses the
 * database-provided primary portal (lowest role priority).
 */
export function resolveLandingRoute(access: AccessProfile | null, next?: string | null): string {
  if (!access || access.status !== 'active') return '/';
  if (isSafeRedirect(next)) {
    const owner = portalForPath(next.split('?')[0] ?? next);
    if (!owner || canAccessPortal(access, owner.key)) return next;
  }
  return access.primaryPortal && isSafeRedirect(access.primaryPortal) ? access.primaryPortal : '/';
}
